/**
 * Crazy Eights bot.
 *
 * - Play: sheds high-value cards first (they cost the most if an opponent goes out), prefers the card that leaves it
 *   able to follow on in its longest suit, and plays into a suit the next player has been seen unable to match so they
 *   must draw. Eights are held back until nothing else plays or the hand is nearly empty.
 * - Naming a suit: the suit it holds most of, leaning towards one the next player lacks.
 * - Drawing is automatic when nothing plays; it passes only when it cannot draw.
 * The bot only ever reads its own seat's view.
 */
import { parseCard, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { crazyEightsBestSuit, crazyEightsHandValue, crazyEightsValue, type CrazyEightsAction, type CrazyEightsLegal, type CrazyEightsView } from './engine.js';

const SEATS = 4;
const rank = (c: CardId) => parseCard(c).rank;
const suit = (c: CardId) => parseCard(c).suit;

/** Suit to name with an eight (`eight` is the card being played, excluded from the count). */
function nameSuit(view: CrazyEightsView, eight: CardId, ctx: BotContext): Suit {
  const rest = view.hand.filter((c) => c !== eight);
  const next = (view.seat! + 1) % SEATS;
  const lacking = new Set(ctx.profile.memory > 0 ? view.lacks[next] : []);
  let best = crazyEightsBestSuit(view.hand, eight);
  let bestScore = -Infinity;
  for (const s of SUITS) {
    const mine = rest.filter((c) => suit(c) === s && rank(c) !== 8);
    // Own length matters most; a suit the next player cannot follow is a bonus.
    const score = mine.length * 10 + crazyEightsHandValue(mine) / 10 + (lacking.has(s) ? 6 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = s;
    }
  }
  return best;
}

function chooseCard(view: CrazyEightsView, playable: CardId[], ctx: BotContext): CrazyEightsAction {
  const me = view.seat!;
  const next = (me + 1) % SEATS;
  const lacking = new Set(ctx.profile.memory > 0 ? view.lacks[next] : []);
  const plain = playable.filter((c) => rank(c) !== 8);
  const eights = playable.filter((c) => rank(c) === 8);
  // Go for the win with an eight when it is the next-to-last card.
  // Weaker bots burn their wilds early; stronger ones keep them as a way out of a bad spot.
  const eager = (ctx.difficulty === 'BEGINNER' || ctx.difficulty === 'EASY') && chance(ctx, ctx.difficulty === 'BEGINNER' ? 0.6 : 0.35);
  const useEight = plain.length === 0 || (view.hand.length <= 2 && eights.length > 0) || (eager && eights.length > 0);
  if (useEight && eights.length > 0) {
    const card = eights[0]!;
    return { type: 'play', card, suit: nameSuit(view, card, ctx) };
  }
  let best = plain[0]!;
  let bestScore = -Infinity;
  for (const c of plain) {
    const others = view.hand.filter((x) => x !== c);
    const sameSuit = others.filter((x) => suit(x) === suit(c) && rank(x) !== 8).length;
    const sameRank = others.filter((x) => rank(x) === rank(c) && rank(x) !== 8).length;
    let score = crazyEightsValue(c) + sameSuit * 3 + sameRank * 1.5;
    if (lacking.has(suit(c))) score += 6;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return { type: 'play', card: best };
}

export const crazyEightsBot: BotEngine<CrazyEightsView, CrazyEightsLegal, CrazyEightsAction> = {
  decideAction(view, legal, ctx) {
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.playable.length === 0) return legal.canDraw ? { type: 'draw' } : { type: 'pass' };
    if (chance(ctx, ctx.profile.mistakeRate)) {
      const card = legal.playable[ctx.rng.int(legal.playable.length)]!;
      return rank(card) === 8 ? { type: 'play', card, suit: crazyEightsBestSuit(view.hand, card) } : { type: 'play', card };
    }
    return chooseCard(view, legal.playable, ctx);
  },

  evaluateState(view) {
    if (view.seat === null) return 0;
    const mine = view.handCounts[view.seat]!;
    const others = view.handCounts.filter((_, s) => s !== view.seat);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (avg - mine) / 12));
  },

  calculateRisk(view) {
    return view.seat === null ? 0 : Math.min(1, crazyEightsHandValue(view.hand) / 100);
  },

  handleTimeout(view, legal) {
    if (legal.playable.length > 0) {
      const card = [...legal.playable].sort((a, b) => Number(rank(a) === 8) - Number(rank(b) === 8) || crazyEightsValue(b) - crazyEightsValue(a))[0]!;
      return rank(card) === 8 ? { type: 'play', card, suit: crazyEightsBestSuit(view.hand, card) } : { type: 'play', card };
    }
    return legal.canDraw ? { type: 'draw' } : { type: 'pass' };
  },
};
